import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef } from "react";
import classNames from "classnames";
import * as IconComponents from "./icons";
import { Typography } from "./Typography";
import { ThemeProviderContext } from "./ThemeProvider";
import { FOCUS_VISIBLE_OUTLINE } from "../utils/css-helpers";
export const variants = [
    "primary",
    "secondary",
    "secondary-neutral",
    "tertiary",
    "tertiary-neutral",
];
const PRIMARY_CLASS_NAMES = [
    "!bg-cta-primary-default",
    "hover:!bg-cta-primary-hover",
    "active:!bg-cta-primary-pressed",
    "!text-text-primary-inverse",
    "!border-transparent",
    "dark:!bg-general-indigo-primary-darkmode",
    "dark:hover:!bg-general-dark-primary-darkmode",
    "dark:hover:!text-text-primary-darkmode",
    "dark:active:!bg-general-indigo-primary-darkmode",
];
const SECONDARY_CLASS_NAMES = [
    "hover:!bg-cta-secondary-hover",
    "active:!bg-cta-secondary-pressed",
    "!text-cta-primary-default",
    "!border-cta-primary-default",
    "dark:hover:!bg-general-dark-secondary-darkmode",
    "dark:active:!bg-background-primary-darkmode",
    "dark:!text-text-brand-darkmode",
    "dark:!border-border-brand-darkmode",
];
const SECONDARY_TRANSPARENT_CLASS_NAMES = [
    ...SECONDARY_CLASS_NAMES,
    "!bg-transparent",
];
const SECONDARY_FILLED_CLASS_NAMES = [
    ...SECONDARY_CLASS_NAMES,
    "!bg-cta-secondary-default dark:!bg-background-primary-darkmode",
];
const SECONDARY_NEUTRAL_CLASS_NAMES = [
    "hover:!bg-general-light-secondary",
    "active:!bg-background-primary",
    "!text-text-primary",
    "dark:!border-border-regular-darkmode",
    "dark:hover:!bg-general-dark-secondary-darkmode",
    "dark:active:!bg-background-primary-darkmode",
    "dark:!text-text-primary-darkmode",
    "dark:!border-border-regular-darkmode",
];
const SECONDARY_NEUTRAL_TRANSPARENT_CLASS_NAMES = [
    ...SECONDARY_NEUTRAL_CLASS_NAMES,
    "!bg-transparent",
];
const SECONDARY_NEUTRAL_FILLED_CLASS_NAMES = [
    ...SECONDARY_NEUTRAL_CLASS_NAMES,
    "!bg-background-primary dark:!bg-background-primary-darkmode",
];
const SECONDARY_NEUTRAL_DARK_MODE_CLASS_NAMES = [
    "!bg-transparent",
    "!border-border-regular-darkmode",
    "hover:!bg-general-dark-secondary-darkmode",
    "active:!bg-background-primary-darkmode",
    "!text-text-primary-darkmode",
    "!border-border-regular-darkmode",
];
const TERTIARY_CLASS_NAMES = [
    "!bg-transparent",
    "hover:!bg-cta-secondary-hover",
    "active:!bg-cta-secondary-pressed",
    "!text-cta-primary-default",
    "!border-transparent",
    "dark:hover:!bg-general-dark-secondary-darkmode",
    "dark:active:!bg-background-primary-darkmode",
    "dark:!text-text-brand-darkmode",
];
const TERTIARY_NEUTRAL_CLASS_NAMES = [
    "!bg-transparent",
    "hover:!bg-general-light-secondary",
    "active:!bg-background-primary",
    "!text-text-primary",
    "!border-transparent",
    "dark:hover:!bg-general-dark-secondary-darkmode",
    "dark:active:!bg-background-primary-darkmode",
    "dark:!text-text-primary-darkmode",
];
const SQUARE_CLASS_NAMES = ["!rounded"];
const PILL_CLASS_NAMES = ["!rounded-full"];
const DISABLED_CLASS_NAMES = [
    "!bg-background-disabled",
    "!border-border-disabled",
    "!text-text-disabled",
    "dark:!bg-background-disabled-darkmode",
    "dark:!border-border-disabled-darkmode",
    "dark:!text-text-disabled-darkmode",
];
export const Button = forwardRef(({ variant = "secondary", size = "medium", shape: shapeExplicit, className = "", iconName, iconPosition = "left", iconTitle, isDisabled, text, textWeight = "normal", onClick, fitting = "content-fit", type = "button", name, testId, isForcedDarkMode = false, backgroundStyle = "transparent", ariaLabel, }, ref) => {
    return (_jsx(ThemeProviderContext.Consumer, { children: ({ defaultButtonShape, testAttribute }) => {
            const shape = shapeExplicit || defaultButtonShape;
            const variantClassName = [
                variant === "primary" ? PRIMARY_CLASS_NAMES : [],
                variant === "secondary" &&
                    backgroundStyle === "filled" &&
                    SECONDARY_FILLED_CLASS_NAMES,
                variant === "secondary" &&
                    backgroundStyle === "transparent" &&
                    SECONDARY_TRANSPARENT_CLASS_NAMES,
                variant === "secondary-neutral" &&
                    isForcedDarkMode &&
                    SECONDARY_NEUTRAL_DARK_MODE_CLASS_NAMES,
                variant === "secondary-neutral" &&
                    !isForcedDarkMode &&
                    backgroundStyle === "transparent" &&
                    SECONDARY_NEUTRAL_TRANSPARENT_CLASS_NAMES,
                variant === "secondary-neutral" &&
                    !isForcedDarkMode &&
                    backgroundStyle === "filled" &&
                    SECONDARY_NEUTRAL_FILLED_CLASS_NAMES,
                variant === "tertiary" ? TERTIARY_CLASS_NAMES : [],
                variant === "tertiary-neutral" ? TERTIARY_NEUTRAL_CLASS_NAMES : [],
            ];
            const isIconOnly = !!iconName && !text;
            let paddingClassNames;
            if (isIconOnly) {
                if (size === "large") {
                    paddingClassNames = ["!p-[10px]"];
                }
                else if (size === "medium") {
                    paddingClassNames = ["!p-1"];
                }
                else {
                    // small
                    paddingClassNames = ["!p-0.5"];
                }
            }
            else {
                if (size === "large") {
                    paddingClassNames = ["!px-1.5", "!py-1"];
                }
                else if (size === "medium") {
                    paddingClassNames = ["!px-1.5", "!py-0.75"];
                }
                else {
                    // small
                    paddingClassNames = ["!px-1", "!py-0.25"];
                }
            }
            const buttonClassName = classNames("!text-center", "inline-flex", "!place-content-center", "!items-center", "!border", "whitespace-nowrap", FOCUS_VISIBLE_OUTLINE, "focus:!outline-transparent", paddingClassNames, shape === "pill" ? PILL_CLASS_NAMES : SQUARE_CLASS_NAMES, isDisabled ? DISABLED_CLASS_NAMES : variantClassName, fitting === "layout-fit" && "!w-full", className);
            const Icon = iconName ? IconComponents[`${iconName}Icon`] : null;
            const iconSize = size === "large" ? "md" : "sm";
            const iconMinWidthClass = size === "large" ? "min-w-[18px]" : "min-w-[14px]";
            return (_jsxs("button", { onClick: onClick, disabled: isDisabled, className: buttonClassName, style: { overflowWrap: "anywhere" }, ref: ref, name: name, type: type, "aria-label": ariaLabel, [testAttribute]: testId, children: [Icon &&
                        iconPosition === "left" &&
                        React.cloneElement(_jsx(Icon, { title: iconTitle, size: iconSize }), {
                            className: classNames(iconMinWidthClass, text && "mr-0.75"),
                        }), _jsx(Typography, { className: size !== "small" && textWeight === "heavy" ? "!font-bold" : "", children: text }), Icon &&
                        iconPosition === "right" &&
                        React.cloneElement(_jsx(Icon, { title: iconTitle, size: iconSize }), {
                            className: classNames(iconMinWidthClass, text && "ml-0.75"),
                        })] }));
        } }));
});
//# sourceMappingURL=Button.js.map